import { useState } from 'react';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { useProfitLoss } from '@/hooks/useAccounting';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatCurrency } from '@/lib/utils';

function startOfYear() {
  return new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
}
function today() { return new Date().toISOString().slice(0, 10); }

export function ProfitLossPage() {
  const [fromDate, setFromDate] = useState(startOfYear());
  const [toDate, setToDate] = useState(today());
  const { data, isLoading, error } = useProfitLoss(fromDate, toDate);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load P&amp;L"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  if (!data || (data.revenue.length === 0 && data.expenses.length === 0)) {
    return (
      <Card>
        <EmptyState
          icon={TrendingUp}
          title="No income or expense activity"
          description="Once you post journal entries to revenue or expense accounts, this statement will populate."
        />
      </Card>
    );
  }

  const margin = data.totalRevenue > 0 ? (data.netIncome / data.totalRevenue) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1">Profit &amp; Loss</div>
          <div className="text-sm text-text-muted">Revenue minus expenses for the selected period.</div>
        </div>
        <div className="flex gap-3">
          <div className="w-40">
            <Input label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="w-40">
            <Input label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Net income callout */}
      <Card>
        <div className="flex items-end justify-between">
          <div>
            <div className="kicker mb-2">Net Income</div>
            <div className={`font-display text-5xl tabular ${
              data.netIncome >= 0 ? 'text-success' : 'text-danger'
            }`}>
              {data.netIncome >= 0 ? '' : '−'}{formatCurrency(Math.abs(data.netIncome))}
            </div>
            <div className="mt-2 text-xs text-text-muted">
              {new Date(fromDate).toLocaleDateString('en-US', { dateStyle: 'medium' })} —{' '}
              {new Date(toDate).toLocaleDateString('en-US', { dateStyle: 'medium' })}
            </div>
          </div>
          <div className="text-right">
            <div className="kicker mb-2">Net Margin</div>
            <Badge tone={margin >= 20 ? 'success' : margin >= 10 ? 'brass' : margin >= 0 ? 'warning' : 'danger'}>
              {margin.toFixed(1)}%
            </Badge>
          </div>
        </div>
      </Card>

      {/* Revenue */}
      <Card className="p-0 overflow-hidden">
        <header className="px-6 py-4 border-b border-border bg-bg-subtle">
          <div className="kicker">Revenue</div>
        </header>
        {data.revenue.length === 0 ? (
          <div className="p-6 text-sm text-text-dim text-center">No revenue in this period.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.revenue.map((r) => (
                <tr key={r.accountId} className="border-b border-border last:border-0 hover:bg-bg-hover/40">
                  <td className="px-6 py-3 font-mono text-brass-300 w-28">{r.accountNumber}</td>
                  <td className="px-6 py-3 text-text">{r.accountName}</td>
                  <td className="px-6 py-3 text-right tabular text-text">{formatCurrency(r.amount)}</td>
                </tr>
              ))}
              <tr className="border-t border-border-strong bg-bg-panel">
                <td colSpan={2} className="px-6 py-3 text-right">
                  <span className="kicker">Total Revenue</span>
                </td>
                <td className="px-6 py-3 text-right tabular font-display text-lg text-success">
                  {formatCurrency(data.totalRevenue)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      {/* Expenses */}
      <Card className="p-0 overflow-hidden">
        <header className="px-6 py-4 border-b border-border bg-bg-subtle">
          <div className="kicker">Expenses</div>
        </header>
        {data.expenses.length === 0 ? (
          <div className="p-6 text-sm text-text-dim text-center">No expenses in this period.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.expenses.map((e) => (
                <tr key={e.accountId} className="border-b border-border last:border-0 hover:bg-bg-hover/40">
                  <td className="px-6 py-3 font-mono text-brass-300 w-28">{e.accountNumber}</td>
                  <td className="px-6 py-3 text-text">{e.accountName}</td>
                  <td className="px-6 py-3 text-right tabular text-text">{formatCurrency(e.amount)}</td>
                </tr>
              ))}
              <tr className="border-t border-border-strong bg-bg-panel">
                <td colSpan={2} className="px-6 py-3 text-right">
                  <span className="kicker">Total Expenses</span>
                </td>
                <td className="px-6 py-3 text-right tabular font-display text-lg text-warning">
                  {formatCurrency(data.totalExpenses)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
